/**
 * M02 意图管理 fixture · S2 Day 6b · CLAUDE.md §11.5.1 数据工厂复用
 *
 * 承接：
 * - mock 基础设施 W2-9 PR-1（registerFixture / resolveFixture）
 * - 后端 schema apps/api/src/csa_api/admin/intent_admin.py
 *
 * 注册时机：apps/admin/src/main.tsx 启动时调用 registerM02Fixtures()
 *
 * 设计意图：
 * - P11 P0 13 intent 全收录（与后端 INTENT_LABELS 同序）
 * - L2 YAML mock 含 4 条快路径 rule（4 启用）· 普通业务语义显示为 L1 主路径
 * - L1 prompt mock：mer_devcons 默认空 B（ratio 0）· mer_test_ab 灰度 v1.1@20%（验证非空 B 渲染）
 */

import { registerFixture } from "./registry";
import type { MockResponse } from "./types";

interface IntentItem {
	intent: string;
	description_en: string;
	description_zh: string;
	service_stage: "pre_sale" | "in_sale" | "after_sale" | "unknown";
	l2_summary: {
		rules_count: number;
		enabled_count: number;
		sample_keywords_zh: string[];
		sample_keywords_en: string[];
	};
}

const L1_PATH_SUMMARY: IntentItem["l2_summary"] = {
	rules_count: 0,
	enabled_count: 0,
	sample_keywords_zh: [],
	sample_keywords_en: [],
};

const MOCK_INTENT_LIST: IntentItem[] = [
	{
		intent: "order_status_inquiry",
		description_en: "user asks about order status / shipping ETA / order info",
		description_zh: "订单状态 / 物流 ETA / 订单信息",
		service_stage: "in_sale",
		l2_summary: {
			rules_count: 1,
			enabled_count: 1,
			sample_keywords_zh: ["订单状态", "查订单", "状态"],
			sample_keywords_en: ["order status", "status"],
		},
	},
	{
		intent: "shipping_tracking",
		description_en: "user asks for tracking progress / estimated arrival",
		description_zh: "物流进度 / 预计到货时间",
		service_stage: "after_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "order_issue",
		description_en: "user reports an issue with a received order/package/item",
		description_zh: "订单 / 包裹 / 商品异常问题",
		service_stage: "after_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "refund_request",
		description_en: "user explicitly asks to get money back / refund",
		description_zh: "明确要求退款 / 拿回款项",
		service_stage: "after_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "return_request",
		description_en: "user wants to physically return goods",
		description_zh: "希望物理退回商品",
		service_stage: "after_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "order_cancel",
		description_en: "user wants to cancel an order",
		description_zh: "希望取消订单",
		service_stage: "in_sale",
		l2_summary: {
			rules_count: 1,
			enabled_count: 1,
			sample_keywords_zh: ["取消订单", "不要发货"],
			sample_keywords_en: ["cancel order", "do not ship"],
		},
	},
	{
		intent: "address_change",
		description_en: "user wants to change delivery address / phone / recipient",
		description_zh: "修改收货地址 / 电话 / 收件人",
		service_stage: "in_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "product_inquiry",
		description_en: "pre-sale product question (specs, availability, fit, material)",
		description_zh: "售前商品问题（规格 / 库存 / 尺码 / 材质）",
		service_stage: "pre_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "product_recommendation",
		description_en: "user asks for product suggestions / recommendations",
		description_zh: "请求商品推荐 / 选品建议",
		service_stage: "pre_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "knowledge_qa",
		description_en: "policy / FAQ / shipping rules / after-sale rules",
		description_zh: "政策 / FAQ / 运费规则 / 售后规则",
		service_stage: "unknown",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "discount_inquiry",
		description_en: "coupon validity / discount rules",
		description_zh: "优惠券 / 折扣规则",
		service_stage: "pre_sale",
		l2_summary: L1_PATH_SUMMARY,
	},
	{
		intent: "human_handoff",
		description_en: "user requests human agent / questions if talking to bot",
		description_zh: "要求转人工 / 怀疑在跟 bot 对话",
		service_stage: "unknown",
		l2_summary: {
			rules_count: 1,
			enabled_count: 1,
			sample_keywords_zh: ["转人工", "客服"],
			sample_keywords_en: ["human", "agent"],
		},
	},
	{
		intent: "unknown",
		description_en: "greeting, small talk, closed topic, or cannot classify",
		description_zh: "问候 / 闲聊 / 已结束话题 / 无法分类",
		service_stage: "unknown",
		l2_summary: {
			rules_count: 1,
			enabled_count: 1,
			sample_keywords_zh: ["你好", "谢谢"],
			sample_keywords_en: ["hello", "thanks"],
		},
	},
];

const MOCK_YAML = `version: "2026-05-25T00:00:00Z"
author: "mock@p11-p0"
schema_version: "1.0"
globals:
  min_score_default: 0.75
  dispatcher_confidence_threshold: 0.75
  ambiguous_margin: 0.15
  match_mode: "word_boundary"
  case_sensitive: false
rules:
  - id: R001
    intent: human_handoff
    enabled: true
    priority: 120
    keywords:
      zh: ["转人工", "人工", "真人", "找人", "换人", "客服"]
      en: ["talk to human", "talk to a human", "real person", "human agent", "representative", "live agent"]
    examples: ["转人工", "I want to talk to a human"]
  - id: R002
    intent: order_cancel
    enabled: true
    priority: 110
    required_patterns:
      - "(?i)(#\\\\d{3,}|\\\\border\\\\s*#?\\\\d{3,}\\\\b|订单号\\\\s*\\\\d{3,}|订单\\\\s*#?\\\\d{3,})"
    keywords:
      zh: ["取消订单", "取消", "不要发货", "别发货"]
      en: ["cancel order", "cancel", "do not ship", "don't ship", "stop the order"]
    examples: ["取消订单 #1001", "Please cancel order #1001"]
  - id: R003
    intent: order_status_inquiry
    enabled: true
    priority: 105
    required_patterns:
      - "(?i)(#\\\\d{3,}|\\\\border\\\\s*#?\\\\d{3,}\\\\b|订单号\\\\s*\\\\d{3,}|订单\\\\s*#?\\\\d{3,})"
    keywords:
      zh: ["订单状态", "订单进度", "查订单", "状态", "到哪了", "发货了吗"]
      en: ["order status", "where is", "status", "order progress", "shipped yet"]
    examples: ["查一下订单 #1001 的状态", "What is the status of order #1001?"]
  - id: R004
    intent: unknown
    enabled: true
    priority: 90
    keywords:
      zh: ["你好", "您好", "谢谢", "感谢", "再见", "没事了"]
      en: ["hello", "hi", "thanks", "thank you", "bye", "goodbye"]
    examples: ["你好", "Thanks"]
`;

interface MockL1Config {
	merchant_id: string;
	l1_prompt_version_a: string;
	l1_prompt_version_b: string | null;
	l1_ab_ratio: number;
	updated_at: string;
}

interface MockL1PromptTemplate {
	version_id: string;
	title: string;
	system_prompt: string;
	few_shot_examples: string;
	notes: string | null;
	updated_by: string;
	updated_at: string;
}

interface MockL1PromptTemplatesResponse {
	yaml_text: string;
	active_version: string;
	templates_total: number;
	current_template: MockL1PromptTemplate;
}

const NOW_ISO = new Date().toISOString();

let mockL1PromptYaml = `active_version: "v1.0"
templates:
  - version_id: "v1.0"
    title: "P11 P0 L1 V0.3 baseline"
    system_prompt: |
      Mock L1 intent classifier system_prompt.
      Output STRICT JSON only.
    few_shot_examples: |
      ### Example 1
      Output: {"intent":"refund_request","dialogue_state":"clarify_needed"}
    notes: "mock baseline"
    updated_by: "mock"
    updated_at: "${NOW_ISO}"
  - version_id: "v1.1"
    title: "P11 P0 L1 V0.3 gray"
    system_prompt: |
      Mock gray L1 intent classifier system_prompt.
      Output STRICT JSON only.
    few_shot_examples: ""
    notes: "mock gray"
    updated_by: "mock"
    updated_at: "${NOW_ISO}"
`;

function parseMockL1PromptTemplates(yamlText: string): MockL1PromptTemplatesResponse {
	const activeMatch = yamlText.match(/active_version:\s*"([^"]+)"/);
	const versionMatches = [...yamlText.matchAll(/version_id:\s*"([^"]+)"/g)].map(
		(match) => match[1],
	);
	const active = activeMatch?.[1] ?? versionMatches[0] ?? "v1.0";
	const titleMatch = yamlText.match(
		new RegExp(`version_id:\\s*"${active}"[\\s\\S]*?title:\\s*"([^"]+)"`),
	);
	return {
		yaml_text: yamlText,
		active_version: active,
		templates_total: versionMatches.length,
		current_template: {
			version_id: active,
			title: titleMatch?.[1] ?? "L1 prompt template",
			system_prompt: "Mock L1 intent classifier system_prompt. Output STRICT JSON only.",
			few_shot_examples: "Mock few-shot examples",
			notes: "mock",
			updated_by: "mock",
			updated_at: NOW_ISO,
		},
	};
}

const MOCK_L1_CONFIGS: Record<string, MockL1Config> = {
	mer_devcons: {
		merchant_id: "mer_devcons",
		l1_prompt_version_a: "v1.0",
		l1_prompt_version_b: null,
		l1_ab_ratio: 0,
		updated_at: NOW_ISO,
	},
	mer_test_ab: {
		merchant_id: "mer_test_ab",
		l1_prompt_version_a: "v1.0",
		l1_prompt_version_b: "v1.1",
		l1_ab_ratio: 20,
		updated_at: NOW_ISO,
	},
};

/**
 * 注册 M02 fixtures · 启动时调用一次
 */
export function registerM02Fixtures(): void {
	registerFixture({
		pathname: "/admin/v1/intent/list",
		methods: ["GET"],
		resolve: () => ({
			data: {
				items: MOCK_INTENT_LIST,
				bundle_version: "2026-05-25T00:00:00Z",
				bundle_author: "mock@p11-p0",
				schema_version: "1.0",
				total_rules: 4,
			},
		}),
	});

	registerFixture({
		pathname: "/admin/v1/intent/l2_rules",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse => {
			if (ctx.method === "PUT") {
				const body = ctx.body as { yaml_text?: string; expected_version?: string | null };
				if (!body?.yaml_text) {
					return { status: 400, data: { detail: "mock: yaml_text 必填" } };
				}
				return {
					data: {
						bundle_version: new Date().toISOString().replace(/\.\d{3}/, ""),
						total_rules: 4,
						cache_invalidated: true,
					},
				};
			}
			return {
				data: {
					yaml_text: MOCK_YAML,
					bundle_version: "2026-05-25T00:00:00Z",
					bundle_author: "mock@p11-p0",
					schema_version: "1.0",
					total_rules: 4,
					enabled_rules: 4,
				},
			};
		},
	});

	registerFixture<MockL1PromptTemplatesResponse | { detail: string }>({
		pathname: "/admin/v1/intent/l1_prompt_templates",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse<MockL1PromptTemplatesResponse | { detail: string }> => {
			if (ctx.method === "PUT") {
				const body = ctx.body as { yaml_text?: string; expected_active_version?: string | null };
				if (!body?.yaml_text) {
					return { status: 400, data: { detail: "mock: yaml_text 必填" } };
				}
				const incoming = parseMockL1PromptTemplates(body.yaml_text);
				const current = parseMockL1PromptTemplates(mockL1PromptYaml);
				if (
					body.expected_active_version &&
					body.expected_active_version !== current.active_version
				) {
					return { status: 409, data: { detail: "mock: L1 prompt 乐观锁冲突" } };
				}
				if (!body.yaml_text.includes(`version_id: "${incoming.active_version}"`)) {
					return { status: 400, data: { detail: "mock: active_version 不在 templates 中" } };
				}
				mockL1PromptYaml = body.yaml_text;
				return { data: incoming };
			}
			return { data: parseMockL1PromptTemplates(mockL1PromptYaml) };
		},
	});

	registerFixture<MockL1Config | { detail: string }>({
		pathname: "/admin/v1/intent/merchants/:merchant_id/l1_prompt",
		methods: ["GET", "PUT"],
		resolve: (ctx): MockResponse<MockL1Config | { detail: string }> => {
			const mid = ctx.params.merchant_id;
			if (!mid) return { status: 404, data: { detail: "mock: merchant_id missing" } };
			const existing = MOCK_L1_CONFIGS[mid];
			if (ctx.method === "GET") {
				if (!existing) return { status: 404, data: { detail: `mock: ${mid} not found` } };
				return { data: existing };
			}
			// PUT
			if (!existing) return { status: 404, data: { detail: `mock: ${mid} not found` } };
			const body = ctx.body as Partial<MockL1Config>;
			if (
				body?.l1_prompt_version_b == null &&
				typeof body?.l1_ab_ratio === "number" &&
				body.l1_ab_ratio !== 0
			) {
				return {
					status: 400,
					data: { detail: "mock: l1_prompt_version_b 为空时 l1_ab_ratio 必须为 0" },
				};
			}
			const updated: MockL1Config = {
				...existing,
				...body,
				merchant_id: mid,
				updated_at: new Date().toISOString(),
			};
			MOCK_L1_CONFIGS[mid] = updated;
			return { data: updated };
		},
	});
}
